class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

if (!customElements.get('cart-drawer-recommendations')) {
  class CartDrawerRecommendations extends HTMLElement {
    constructor() {
      super();
      this.swiper = null;
      this.isFetching = false;
      this.abortController = null;
      // Store bound handler reference so it can be removed in disconnectedCallback
      this.clickHandler = this.handleClick.bind(this);
    }

    connectedCallback() {
      // Products already rendered server-side (manual products or API response injected by JS)
      if (this.dataset.loaded === 'true') {
        this.initSwiper();
        this.addEventListener('click', this.clickHandler);
        return;
      }

      // API fetch path: data-url is set when cart has items and no manual products
      if (this.dataset.url) {
        this.fetchRecommendations();
      }
    }

    disconnectedCallback() {
      // Cancel any in-flight fetch so it doesn't mutate a detached element
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.destroySwiper();
      this.removeEventListener('click', this.clickHandler);
    }

    async fetchRecommendations() {
      if (this.isFetching) return;
      this.isFetching = true;
      this.classList.add('loading');

      this.abortController = new AbortController();

      try {
        const response = await fetch(this.dataset.url, { signal: this.abortController.signal });
        if (!response.ok) throw new Error(`Recommendations fetch failed: ${response.status}`);

        const html = await response.text();

        // Element may have been removed while fetch was in-flight (e.g. cart update)
        if (!this.isConnected) return;

        const parsed = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('cart-drawer-recommendations');

        if (parsed && parsed.dataset.loaded === 'true') {
          this.innerHTML = parsed.innerHTML;
          this.dataset.loaded = 'true';
          this.initSwiper();
          this.addEventListener('click', this.clickHandler);
        } else {
          // No recommendations returned — remove the element silently
          this.remove();
        }
      } catch (error) {
        if (error.name === 'AbortError') return;
        console.error('[CartDrawerRecommendations]', error);
        if (this.isConnected) this.remove();
      } finally {
        this.isFetching = false;
        this.abortController = null;
        if (this.isConnected) this.classList.remove('loading');
      }
    }

    initSwiper() {
      // Guard: don't init on a detached element (e.g. pending setTimeout retry after removal)
      if (!this.isConnected) return;

      const swiperEl = this.querySelector('.swiper');
      if (!swiperEl) return;

      if (typeof Swiper === 'undefined') {
        setTimeout(() => this.initSwiper(), 50);
        return;
      }

      this.destroySwiper();

      this.swiper = new Swiper(swiperEl, {
        slidesPerView: 1,
        spaceBetween: 10,
        loop: false,
        navigation: {
          nextEl: this.querySelector('.cart-drawer-recommendations__nav-btn--next'),
          prevEl: this.querySelector('.cart-drawer-recommendations__nav-btn--prev'),
        },
      });
    }

    destroySwiper() {
      if (this.swiper && typeof this.swiper.destroy === 'function') {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }

    handleClick(event) {
      const btn = event.target.closest('.cart-drawer-recommendations__add-btn');
      if (!btn || btn.disabled || btn.classList.contains('loading')) return;

      const variantId = parseInt(btn.dataset.variantId, 10);
      if (!variantId) return;

      this.addToCart(btn, variantId);
    }

    async addToCart(btn, variantId) {
      const spinner = btn.querySelector('.cart-drawer-recommendations__add-spinner');
      if (spinner) spinner.classList.remove('hidden');
      btn.classList.add('loading');
      btn.setAttribute('disabled', '');

      try {
        const response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
        });

        if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

        // Trigger Dawn's cart update — this will replace the entire drawer inner HTML,
        // so no need to manually restore button state on success.
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-recommendations' });
        }
      } catch (error) {
        console.error('[CartDrawerRecommendations] addToCart:', error);
        // Only restore button state on failure — success triggers a full re-render
      } finally {
        if (spinner) spinner.classList.add('hidden');
        btn.classList.remove('loading');
        btn.removeAttribute('disabled');
      }
    }
  }

  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
